import React from 'react'
import { Avatar, Button, BaseFileUpload, RightDrawer } from '@/shared/ui'
import { BottomDrawer } from '@/shared/ui/BottomDrawer'
import { useUserDateFormatter } from '@/modules/auth/hooks/useUserTimezone'
import type { ResultsThreadHook } from '../hooks/useResultsThread'

type ResultsDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunityId: number
  symbol: string
  name?: string | null
  logoUrl?: string | null
  threadHook: ResultsThreadHook
  currentUserId: string | null
  isMaster: boolean
}

export function ResultsDrawer({
  open,
  onOpenChange,
  symbol,
  name,
  logoUrl,
  threadHook,
  currentUserId,
  isMaster,
}: ResultsDrawerProps) {
  const isLg =
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? true
      : window.matchMedia('(min-width: 1024px)').matches

  const { results, loading, loadedOnce, sending, sendResult, deleteResult } = threadHook
  const { formatUserDateTime } = useUserDateFormatter()

  const [pendingFiles, setPendingFiles] = React.useState<File[]>([])
  const [previews, setPreviews] = React.useState<string[]>([])
  const [error, setError] = React.useState<string | null>(null)

  const handleFilesSelected = React.useCallback((files: File[]) => {
    const raw = Array.isArray(files) ? files : []
    const images = raw.filter(
      (file) => file && typeof file.type === 'string' && file.type.startsWith('image/'),
    )
    if (!images.length) return

    const MAX_BYTES = 1_000_000
    const accepted: File[] = []
    let hasTooBig = false

    images.forEach((file) => {
      if (file.size > MAX_BYTES) {
        hasTooBig = true
        return
      }
      accepted.push(file)
    })

    if (!accepted.length) {
      setError('Não é possível enviar imagens acima de 1 MB por arquivo.')
      setPendingFiles([])
      setPreviews([])
      return
    }

    if (hasTooBig) {
      setError('Algumas imagens foram ignoradas por exceder 1 MB.')
    } else if (error) {
      setError(null)
    }

    setPendingFiles(accepted)
    setPreviews([])

    accepted.forEach((file) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result
        if (typeof result !== 'string') return
        setPreviews((prev) => [...prev, result])
      }
      try {
        reader.readAsDataURL(file)
      } catch {
        // silencioso
      }
    })
  }, [error])

  const handleSend = React.useCallback(async () => {
    if (!pendingFiles.length || sending) return

    try {
      await sendResult(pendingFiles)
      setPendingFiles([])
      setPreviews([])
      setError(null)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ResultsDrawer] sendResult error', e)
      setError('Falha ao enviar resultado. Tente novamente.')
    }
  }, [pendingFiles, sendResult, sending])

  const header = (
    <div className="flex items-center gap-2">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={name || symbol}
          className="h-6 w-6 rounded-full object-cover"
        />
      ) : (
        <div className="h-6 w-6 rounded-full bg-muted" />
      )}
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold">{name || symbol}</div>
        <div className="truncate text-xs text-foreground/70">{symbol}</div>
      </div>
    </div>
  )

  const content = (
    <div className="flex h-full flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        {loading && !loadedOnce && (
          <div className="text-sm text-foreground/70">Carregando resultados...</div>
        )}

        {loadedOnce && results.length === 0 && (
          <div className="text-sm text-foreground/60">
            Nenhum resultado enviado ainda para esta oportunidade.
          </div>
        )}

        <div className="space-y-4">
          {results.map((res) => {
            const createdLabel = res.createdAt
              ? formatUserDateTime(res.createdAt)
              : null
            const canDelete =
              !!currentUserId &&
              (res.authorId === currentUserId || isMaster)

            return (
              <div
                key={res.id}
                className="flex gap-3 rounded-md bg-[#161616] p-3"
              >
                <Avatar
                  src={res.authorAvatarUrl || undefined}
                  alt={res.authorName}
                  className="h-8 w-8 flex-shrink-0 border-none"
                  fallback={res.authorName?.[0] || 'U'}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <div className="truncate font-semibold text-foreground">
                      {res.authorName}
                    </div>
                    {createdLabel && (
                      <div className="whitespace-nowrap text-[11px] text-foreground/60">
                        {createdLabel}
                      </div>
                    )}
                  </div>

                  <div className="mt-2 space-y-2">
                    {res.images.map((img) => (
                      <div
                        key={img.id}
                        className="overflow-hidden rounded-md bg-black/60 p-1"
                      >
                        <img
                          src={img.url}
                          alt="Imagem de resultado"
                          className="max-h-[320px] w-full rounded-md object-contain"
                        />
                      </div>
                    ))}
                  </div>

                  {canDelete && (
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => deleteResult(res.id)}
                        className="text-[11px] font-medium text-red-400 hover:text-red-300"
                      >
                        Excluir
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="mt-3 border-t border-border/60 pt-3">
        {error && (
          <div className="mb-2 text-[11px] text-red-400">
            {error}
          </div>
        )}

        {previews.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {previews.map((src, index) => (
              <div
                key={`${index}-${src}`}
                className="relative h-16 w-16 overflow-hidden rounded-md border border-border/60 bg-[#111111]"
              >
                <img
                  src={src}
                  alt="Pré-visualização do resultado"
                  className="h-full w-full object-cover"
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:w-auto">
            <BaseFileUpload
              accept="image/*"
              multiple={false}
              onFiles={handleFilesSelected}
            />
          </div>
          <div className="flex w-full justify-end sm:w-auto">
            <Button
              type="button"
              variant="outline"
              disabled={!pendingFiles.length || sending}
              onClick={handleSend}
              className="h-9 px-3 text-xs"
            >
              Enviar resultado
            </Button>
          </div>
        </div>
      </div>
    </div>
  )

  return isLg ? (
    <RightDrawer
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      className="w-[30vw] max-w-none bg-[#1a1a1a]"
      overlayClassName="bg-black/40 backdrop-blur-md"
      variant="high"
    >
      {content}
    </RightDrawer>
  ) : (
    <BottomDrawer
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      className="h-[100dvh] max-h-[100vh] bg-[#1a1a1a]"
      zIndexClass="z-[100000]"
      overlayClassName="bg-black/40 backdrop-blur-md"
    >
      {content}
    </BottomDrawer>
  )
}
